import type { Participant, Tour, TourSettings } from '@konvoi/shared';

/**
 * Die HTTP-Schnittstelle des Backends.
 *
 * Läuft unter derselben Adresse wie die Oberfläche, deshalb genügen relative
 * Wege. Fehler kommen als deutscher Satz zurück und werden auch so gezeigt —
 * ein Fahrer soll lesen können, was los ist, nicht einen Fehlercode deuten.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; token?: string; headers?: Record<string, string> } = {},
): Promise<T> {
  let antwort: Response;

  try {
    antwort = await fetch(path, {
      method: options.method ?? 'GET',
      headers: {
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(options.token === undefined ? {} : { authorization: `Bearer ${options.token}` }),
        ...options.headers,
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ApiError(0, 'offline', 'Keine Verbindung. Prüfe den Empfang und versuche es erneut.');
  }

  if (antwort.status === 204) {
    return undefined as T;
  }

  const inhalt = (await antwort.json().catch(() => null)) as
    | { error?: { code?: string; message?: string } }
    | null;

  if (!antwort.ok) {
    throw new ApiError(
      antwort.status,
      inhalt?.error?.code ?? 'unbekannt',
      inhalt?.error?.message ?? 'Da ist etwas schiefgegangen.',
    );
  }

  return inhalt as T;
}

export interface CreatedTour {
  tour: Tour;
  joinCode: string;
  joinUrl: string;
  settings: TourSettings;
}

export interface TourPreview {
  tour: Tour;
  participantCount: number;
}

export interface JoinResult {
  tour: Tour;
  settings: TourSettings;
  participants: Participant[];
  you: Participant;
  sessionToken: string;
  webSocketUrl: string;
}

export interface TourStateResult {
  tour: Tour;
  settings: TourSettings;
  participants: Participant[];
  youId: string;
}

export const api = {
  createTour: (name: string, organizerPassword: string, startsAt: number | null = null) =>
    request<CreatedTour>('/api/tours', {
      method: 'POST',
      body: { name, startsAt },
      headers: { 'x-organizer-password': organizerPassword },
    }),

  tourByCode: (joinCode: string) => request<TourPreview>(`/api/tours/by-code/${joinCode}`),

  join: (
    joinCode: string,
    body: {
      deviceId: string;
      displayName: string;
      licensePlate: string;
      vehicleLabel: string | null;
      vehicleYear: number | null;
    },
  ) => request<JoinResult>(`/api/tours/by-code/${joinCode}/join`, { method: 'POST', body }),

  state: (tourId: string, token: string) =>
    request<TourStateResult>(`/api/tours/${tourId}/state`, { token }),

  updateProfile: (
    tourId: string,
    token: string,
    body: {
      displayName?: string;
      licensePlate?: string;
      vehicleLabel?: string | null;
      vehicleYear?: number | null;
      color?: string;
      visible?: boolean;
    },
  ) => request<{ participant: Participant }>(`/api/tours/${tourId}/me`, {
    method: 'PATCH',
    body,
    token,
  }),

  leave: (tourId: string, token: string) =>
    request<void>(`/api/tours/${tourId}/leave`, { method: 'POST', token }),

  setRole: (tourId: string, token: string, participantId: string, role: string) =>
    request<{ participants: Participant[] }>(`/api/tours/${tourId}/roles`, {
      method: 'POST',
      body: { participantId, role },
      token,
    }),

  setTourState: (tourId: string, token: string, state: string) =>
    request<{ tour: Tour }>(`/api/tours/${tourId}/state`, {
      method: 'POST',
      body: { state },
      token,
    }),

  updateSettings: (tourId: string, token: string, changes: Partial<TourSettings>) =>
    request<{ settings: TourSettings }>(`/api/tours/${tourId}/settings`, {
      method: 'PATCH',
      body: changes,
      token,
    }),

  acknowledgeStatusMessage: (tourId: string, token: string, messageId: string) =>
    request<unknown>(`/api/tours/${tourId}/status-messages/${messageId}/ack`, {
      method: 'POST',
      token,
    }),

  ownTrackUrl: (tourId: string) => `/api/tours/${tourId}/me/track.gpx`,
};
